/**
 * Fixtures for Payment & Verification. Document verification and the payout
 * method on file read their own endpoints (`/v1/kyc-verification/status/`,
 * `/v2/get/payment-verification/`), as do Veriff (`/v3/get/kyc/status/`) and
 * contracts (`/v3/get/contract/user-list/`). The payout methods below are the
 * picker's catalogue, not account data.
 */

/*
 * Form rows, keyed on the `details` names `/v2/get/payment-verification/`
 * returns (Rise, Wise, ACH and SWIFT are confirmed; WIRE is not yet seen), so
 * the payload reads straight in with no rename layer. A row is one of:
 *   { name, label, type?, placeholder?, hint?, optional?, half?,
 *     digits?, minLength? }                                        a text input
 *   { name, label, kind: 'country' }                              country picker (the payload holds the name)
 *   { name, label, kind: 'radio', options, initial }              radio group
 *   { kind: 'heading', label }                                    section title
 *   { kind: 'notice', text }                                      warning box
 * `half` rows pair up two to a line on a wide form; everything else spans it.
 */

const HOLDER = { name: 'account_name', label: 'Full name of the account holder', placeholder: 'As shown on your bank statement' };
const ACCOUNT_NUMBER = { name: 'account_number', label: 'Account number', placeholder: 'e.g. 000123456789', half: true };
const ACCOUNT_TYPE = {
  name: 'account_type',
  label: 'Account type',
  kind: 'radio',
  initial: 'Savings',
  // The payload carries the label itself ("Savings").
  options: [
    { value: 'Savings', label: 'Savings' },
    { value: 'Current', label: 'Current' },
  ],
};

/** The recipient's address block every bank transfer ends with. */
const address = (label) => [
  { kind: 'heading', label: "Recipient's address" },
  { name: 'country', label: 'Country', kind: 'country', half: true },
  { name: 'city', label: 'City', placeholder: 'e.g. Bogotá', half: true },
  { name: 'address', label, placeholder: 'Street and number' },
  { name: 'postal_code', label: 'Post code', placeholder: 'e.g. 110111', half: true },
];

/**
 * Bank Wire's three rails, each its own form (the ACH / WIRE / SWIFT tabs).
 * Rows with the same `name` are one value, so switching tabs keeps the holder's
 * name and address rather than asking for them again.
 */
export const BANK_TRANSFERS = [
  {
    id: 'ach',
    label: 'ACH',
    fields: [
      HOLDER,
      { name: 'ach_routing_number', label: 'ACH routing number', placeholder: '9 digits', digits: 9, half: true },
      ACCOUNT_NUMBER,
      ...address('Address'),
      ACCOUNT_TYPE,
    ],
  },
  {
    id: 'wire',
    label: 'WIRE',
    fields: [
      HOLDER,
      // Named after ACH's `ach_routing_number`; no WIRE payload seen yet.
      { name: 'wire_routing_number', label: 'Wire routing number (ABA)', placeholder: '9 digits', digits: 9, half: true },
      ACCOUNT_NUMBER,
      ACCOUNT_TYPE,
      {
        kind: 'notice',
        text: 'Your recipient may be charged an additional fee by their bank for incoming wire transfers. The fee may vary from $12 to $20.',
      },
      ...address("Recipient's address"),
    ],
  },
  {
    id: 'swift',
    label: 'SWIFT',
    fields: [
      HOLDER,
      { name: 'swift_bic_code', label: 'SWIFT / BIC code', placeholder: 'e.g. NWBKGB2L', half: true },
      // The same `account_number` as ACH and WIRE — here it holds the IBAN.
      {
        ...ACCOUNT_NUMBER,
        label: 'IBAN / account number',
        placeholder: 'e.g. GB29 NWBK 6016 1331 9268 19',
        minLength: 8,
      },
      ACCOUNT_TYPE,
      ...address("Recipient's address"),
    ],
  },
];

/**
 * The payout rails a trader can pick, in the order the design draws them.
 * `verification_type` is the API's own value for the method, both ways.
 */
export const PAYMENT_METHODS = [
  {
    id: 'rise',
    verification_type: 'RISE',
    name: 'Rise',
    tagline: 'Fastest — funds in 24 hours',
    meta: ['USD · USDC', 'No fee'],
    fields: [
      {
        name: 'rise_email',
        label: 'Rise account email',
        type: 'email',
        placeholder: 'you@example.com',
        hint: 'The email your Rise account is registered with.',
      },
    ],
  },
  {
    id: 'wise',
    verification_type: 'WISE',
    name: 'Wise',
    tagline: 'Local currency to 40+ countries',
    meta: ['1–2 business days', 'Low fee'],
    fields: [
      {
        name: 'wise_email',
        label: 'Email of the account holder',
        type: 'email',
        placeholder: 'you@example.com',
        hint: 'The email your Wise account is registered with.',
      },
      {
        name: 'wise_tag',
        label: 'Wise tag',
        placeholder: 'e.g. @yourname',
        optional: true,
        hint: 'Optional — find it under Account › Wisetag in the Wise app.',
      },
    ],
  },
  {
    id: 'bank_wire',
    verification_type: 'Bank Wire',
    name: 'Bank Wire',
    tagline: 'ACH, domestic wire or SWIFT',
    meta: ['1–5 business days', 'Bank fees apply'],
    // Picked per rail — see BANK_TRANSFERS.
    transfers: BANK_TRANSFERS,
  },
  {
    id: 'crypto',
    name: 'Crypto',
    tagline: 'USDT and USDC wallets',
    meta: ['Coming soon'],
    comingSoon: true,
    fields: [],
  },
];

